import { createSelector } from 'reselect';

import { ChordType, Component, LineLocation } from '@paragon/contract-library';
import { Direction2D, LineSegment2D, Point2D } from '@paragon/geometry-library';
import { calculateLine } from '@paragon/relative-geometry';
import { calculateMemberPolygon } from '@paragon/three-shared';
import { groupTransitively, notNullish } from '@paragon/utilities';

import { RootState } from '..';
import { SnappingLevel } from '../slices/applicationSlice';
import { getActiveComponent } from './getActiveComponentSelector';
import { getInsertingObjectInfo } from './insertionObjectSelectors';

export interface BearingSnapMetadata {
  /**
   * Specifies which side of the bearing is justified against its definition line. This is from the bearing's frame of
   * reference, therefore it is not guaranteed to correspond with the definitionLineLocation property on the bearing's
   * relative geometry.
   */
  justification: LineLocation;
  /**
   * "Bottom" corresponds to Horizontal 0 on the truss, while "Left" and "Right" correspond to Vertical 0 and Vertical
   * Max, respectively.
   */
  trussSide: 'Bottom' | 'Left' | 'Right';
  /**
   * The chord type for the snap. Helpful for vertical generatable bearings.
   */
  chordType: ChordType;
  /**
   * The guid of the reference line along which the bearing is being snapped, which would become the "bearing reference
   * line".
   */
  referenceLineGuid: string;
}

export type BearingInsertion = { bearingDefinitionPoint: Point2D } & BearingSnapMetadata;

export type BearingInsertionSnap = ({ type: 'segment'; item: LineSegment2D } | { type: 'point'; item: Point2D }) &
  BearingSnapMetadata;

export interface BearingInsertionInfo {
  snappingDistance: SnappingLevel;

  snaps: readonly BearingInsertionSnap[];
}

export const getBearingInsertionInfo = createSelector(
  [getInsertingObjectInfo, getActiveComponent, (state: RootState) => state.application.snappingLevel],
  (insertingObjectInfo, activeComponent, snappingLevel): BearingInsertionInfo | null => {
    if (!activeComponent) return null;

    if (
      insertingObjectInfo?.type !== 'bearing' &&
      (insertingObjectInfo?.type !== 'componentDesign' || !insertingObjectInfo.insertingBearing)
    )
      return null;

    return { snappingDistance: snappingLevel, snaps: calculateBearingInsertionInfo(activeComponent) };
  },
);

interface ChordEdge {
  readonly edge: LineSegment2D;
  readonly chordType: ChordType;
}

export function calculateBearingInsertionInfo(component: Component): BearingInsertionSnap[] {
  // Calculate V0 & VMax
  const verticalLinesToInsertAlong = (
    [
      { lineGuid: component.verticalZeroGuid, trussSide: 'Left' as 'Left' | 'Right' | 'Bottom' },
      { lineGuid: component.verticalMaxGuid, trussSide: 'Right' },
    ] as const
  )
    .map(({ lineGuid, trussSide }) => {
      const lineDefinition = component.referenceLines?.find(({ guid }) => guid === lineGuid);
      const line = lineDefinition && calculateLine(lineDefinition, component);
      return line && lineGuid != null ? { line, lineGuid, trussSide } : null;
    })
    .filter(notNullish);

  const horizontalLinesToInsertAlong = (component.referenceLines ?? [])
    .map((referenceLine) => {
      const line = calculateLine(referenceLine, component);
      if (!line || !line.isParallelTo({ direction: Direction2D.left })) return null;

      return { line, lineGuid: referenceLine.guid, trussSide: 'Bottom' as const };
    })
    .filter(notNullish);

  const linesToInsertAlong = verticalLinesToInsertAlong.concat(horizontalLinesToInsertAlong);

  // Find members whose edges overlap the lines found above
  const chordEdges = component.members
    .map((member) => {
      const polygon = calculateMemberPolygon(member, component);
      const chordType: ChordType | null =
        member.memberType === 'BottomChord' ? 'Bottom' : member.memberType === 'TopChord' ? 'Top' : null;
      if (!polygon || chordType == null) return null;
      return { polygon, chordType: chordType };
    })
    .filter(notNullish)
    .flatMap(({ polygon, chordType }) => polygon.edges.map((edge) => ({ edge, chordType })));

  const touch = (chordEdge1: ChordEdge, chordEdge2: ChordEdge) =>
    chordEdge1.chordType === chordEdge2.chordType &&
    (chordEdge1.edge.endPoints.some((endPoint) => chordEdge2.edge.contains(endPoint)) ||
      chordEdge2.edge.endPoints.some((endPoint) => chordEdge1.edge.contains(endPoint)));

  return linesToInsertAlong.flatMap(({ line, lineGuid, trussSide }) => {
    // This orientation-based approach avoids snapping a bearing to the inside of a member.
    const allowedEdgeDirection =
      trussSide === 'Left' ? Direction2D.down : trussSide === 'Right' ? Direction2D.up : Direction2D.right;
    const chordEdgesAlongLine = chordEdges.filter(
      (chordEdge) => line.containsSegment(chordEdge.edge) && chordEdge.edge.direction.equals(allowedEdgeDirection),
    );
    // Combine any touching segments along a line so that we can treat all members as a single interface.
    return groupTransitively(chordEdgesAlongLine, touch)
      .map((group) => {
        if (group.length === 1) return group[0];
        const allPointsProjected = group
          .flatMap((chordEdge) => chordEdge.edge.endPoints)
          .map((endpoint) => endpoint.projectedSignedDistanceAlong(line));
        const [start, end] = [Math.min(...allPointsProjected), Math.max(...allPointsProjected)].map((extremum) =>
          line.getPointAlongLine(extremum),
        );
        return { edge: new LineSegment2D(start, end), chordType: group[0].chordType };
      })
      .flatMap((combinedChordEdge): BearingInsertionSnap[] => {
        const orientedSegment = combinedChordEdge.edge.orientWith(
          trussSide === 'Bottom' ? Direction2D.right : Direction2D.down,
        );

        // Return each segment found, with additional snaps at their endpoints.
        return [
          {
            type: 'point',
            item: orientedSegment.basePoint,
            justification: trussSide === 'Right' ? 'Starboard' : 'Port',
            trussSide,
            chordType: combinedChordEdge.chordType,
            referenceLineGuid: lineGuid,
          },
          {
            type: 'segment',
            item: orientedSegment,
            justification: trussSide === 'Left' ? 'Starboard' : trussSide === 'Right' ? 'Port' : 'Center',
            trussSide,
            chordType: combinedChordEdge.chordType,
            referenceLineGuid: lineGuid,
          },
          {
            type: 'point',
            item: orientedSegment.endPoint,
            justification: trussSide === 'Right' ? 'Port' : 'Starboard',
            trussSide,
            chordType: combinedChordEdge.chordType,
            referenceLineGuid: lineGuid,
          },
        ];
      });
  });
}
